import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, AlertTriangle, Building2, Mail, Calendar, 
  User, CheckCircle, XCircle, Users, AlertCircle, 
  Loader2, Clock, Target, Shield 
} from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import superAdminServices from '../../../../services/superAdminServices';

export default function AllRisksDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [riskData, setRiskData] = useState(null);

  useEffect(() => {
    if (id) {
      fetchRiskDetails();
    }
  }, [id]);

  const fetchRiskDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await superAdminServices.getRiskDetails(authTokens.access, id);
      setRiskData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'approved': 'bg-green-100 text-green-700',
      'rejected': 'bg-red-100 text-red-700'
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
        {status === 'pending' && <Clock className="h-4 w-4 mr-1" />}
        {status === 'approved' && <CheckCircle className="h-4 w-4 mr-1" />}
        {status === 'rejected' && <XCircle className="h-4 w-4 mr-1" />}
        {status === 'pending' ? 'En attente' : 
         status === 'approved' ? 'Approuvé' : 
         status === 'rejected' ? 'Rejeté' : status}
      </span>
    );
  };

  const getProbabilityBadge = (probability) => {
    const probabilityColors = {
      'mineur': 'bg-blue-100 text-blue-700 border-blue-200',
      'normal': 'bg-green-100 text-green-700 border-green-200',
      'majeur': 'bg-orange-100 text-orange-700 border-orange-200',
      'critique': 'bg-red-100 text-red-700 border-red-200'
    };

    const colorClass = probabilityColors[probability] || 'bg-gray-100 text-gray-700 border-gray-200';

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${colorClass}`}>
        <Target className="h-4 w-4 mr-1" />
        {probability === 'mineur' ? 'Mineur' : 
         probability === 'normal' ? 'Normal' : 
         probability === 'majeur' ? 'Majeur' : 
         probability === 'critique' ? 'Critique' : probability}
      </span>
    );
  };

  const getActionCard = (action) => {
    const isOverdue = action.dueDate && new Date(action.dueDate) < new Date();
    
    return (
      <div key={action.id} className={`p-4 rounded-lg border ${isOverdue ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 mb-2">{action.action}</p>
            
            {action.dueDate && (
              <div className="flex items-center mb-2">
                <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                  Échéance: {new Date(action.dueDate).toLocaleDateString('fr-FR')}
                  {isOverdue && ' (En retard)'}
                </span>
              </div>
            )}
            
            <div className="space-y-1">
              {action.assigned_user && (
                <div className="flex items-center">
                  <User className="h-3 w-3 text-gray-400 mr-1" />
                  <span className="text-xs text-gray-600">
                    Assigné à: {action.assigned_user.full_name || action.assigned_user.email}
                  </span>
                </div>
              )}
              
              {action.assigned_role && (
                <div className="flex items-center">
                  <Shield className="h-3 w-3 text-gray-400 mr-1" />
                  <span className="text-xs text-gray-600">
                    Rôle: {action.assigned_role.name}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {isOverdue && (
            <div className="ml-2">
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                <AlertCircle className="h-3 w-3 mr-1" />
                En retard
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="ml-2 text-sm text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!riskData) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="text-center py-12">
          <p className="text-gray-500">Aucune donnée de risque trouvée</p>
        </div>
      </div>
    );
  }

  const { risk, organization, created_by, last_updated_by, actions } = riskData;

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin-panel/risks')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{risk.risk_name}</h1>
            <p className="text-xs text-gray-500">Détails du risque</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {getStatusBadge(risk.status)}
          {getProbabilityBadge(risk.probability)}
        </div>
      </div>

      <div className="space-y-6">
        {/* Risk Information Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-5 w-5 text-orange-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900">Informations du risque</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Nom du risque</label>
                  <p className="text-sm text-gray-900">{risk.risk_name}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Description</label>
                  <p className="text-sm text-gray-900">{risk.description || 'Aucune description'}</p>
                </div>
              </div>
            </div>
            
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Probabilité</label>
                  <div className="mt-1">
                    {getProbabilityBadge(risk.probability)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Statut</label>
                  <div className="mt-1">
                    {getStatusBadge(risk.status)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Créé le</label>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">
                      {new Date(risk.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Organization Information Card */}
        {organization && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Organisation</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Nom de l'organisation</label>
                    <p className="text-sm text-gray-900">{organization.name}</p>
                  </div>
                  {organization.description && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Description</label>
                      <p className="text-sm text-gray-900">{organization.description}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Statut d'abonnement</label>
                    <div className="mt-1">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        organization.is_subscribed 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {organization.is_subscribed ? 'Abonné' : 'Non abonné'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Organisation créée le</label>
                    <p className="text-sm text-gray-900">
                      {new Date(organization.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Creator and Updater Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Created By */}
          {created_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Créé par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">
                  {created_by.full_name || `${created_by.first_name} ${created_by.last_name}`.trim() || 'Nom non renseigné'}
                </p>
                <div className="flex items-center">
                  <Mail className="h-3 w-3 text-gray-400 mr-1" />
                  <p className="text-xs text-gray-600">{created_by.email}</p>
                </div>
              </div>
            </div>
          )}

          {/* Last Updated By */}
          {last_updated_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Dernière modification par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">
                  {last_updated_by.full_name || `${last_updated_by.first_name} ${last_updated_by.last_name}`.trim() || 'Nom non renseigné'}
                </p>
                <div className="flex items-center">
                  <Mail className="h-3 w-3 text-gray-400 mr-1" />
                  <p className="text-xs text-gray-600">{last_updated_by.email}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions Information Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Shield className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Actions du risque</h2>
            </div>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <span>{actions.statistics.total_actions} action(s) total</span>
              <span className="text-blue-600">{actions.statistics.actions_with_user} assignée(s)</span>
              <span className="text-red-600">{actions.statistics.overdue_actions} en retard</span>
            </div>
          </div>

          {actions.actions_list.length > 0 ? (
            <div className="space-y-3">
              {actions.actions_list.map((action) => getActionCard(action))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Aucune action définie pour ce risque</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
